import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { loginSchema, type LoginInput } from '@munawb/validators';
import { useAuthStore } from '@munawb/hooks';
import { supabase } from '@munawb/supabase';
import type { ProfileWithDept } from '@munawb/types';
import { getRoleRoute } from '../routes/index';
import { useNavigate } from '@tanstack/react-router';

const SUPABASE_URL     = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`timeout:${label}`)), ms)
    ),
  ]);
}

export default function LoginPage() {
  const [serverError, setServerError] = useState<string | null>(null);
  const [step, setStep]               = useState<string>('');
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (data: LoginInput) => {
    setServerError(null);
    setStep('');

    try {
      // ── الخطوة 1: raw fetch مباشرة لـ Supabase Auth ─────────
      setStep('جارٍ التحقق من الهوية...');

      const authRes = await withTimeout(
        fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'apikey':        SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email: data.email, password: data.password }),
        }),
        15_000,
        'signIn'
      );

      const authJson = await authRes.json();

      if (!authRes.ok) {
        const errMsg = authJson?.error_description ?? authJson?.msg ?? authJson?.error ?? 'خطأ في المصادقة';
        throw new Error(errMsg);
      }

      const authSession = authJson as {
        access_token:  string;
        refresh_token: string;
        expires_in:    number;
        token_type:    string;
        user:          { id: string };
      };

      // ── الخطوة 2: تخزين الجلسة في localStorage مباشرة ───────
      // يتجنب استدعاء supabase.auth.setSession() الذي يُجري طلبات شبكة إضافية
      setStep('جارٍ تحميل الصلاحيات...');
      const projectRef = new URL(SUPABASE_URL).hostname.split('.')[0];
      const storageKey = `sb-${projectRef}-auth-token`;
      const sessionPayload = {
        access_token:  authSession.access_token,
        token_type:    authSession.token_type ?? 'bearer',
        expires_in:    authSession.expires_in,
        expires_at:    Math.floor(Date.now() / 1000) + (authSession.expires_in ?? 3600),
        refresh_token: authSession.refresh_token,
        user:          authSession.user,
      };
      localStorage.setItem(storageKey, JSON.stringify(sessionPayload));

      // ── الخطوة 3: جلب الملف الشخصي عبر raw fetch ────────────
      const profileRes = await withTimeout(
        fetch(
          `${SUPABASE_URL}/rest/v1/profiles?select=*,department:departments(id,name)&id=eq.${authSession.user.id}`,
          {
            headers: {
              'apikey':        SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${authSession.access_token}`,
              'Accept':        'application/vnd.pgrst.object+json',
            },
          }
        ),
        10_000,
        'fetchProfile'
      );

      if (!profileRes.ok) {
        const errJson = await profileRes.json().catch(() => ({}));
        throw new Error(errJson?.message ?? `profile fetch failed: ${profileRes.status}`);
      }

      const profileData = await profileRes.json() as ProfileWithDept;
      if (!profileData) throw new Error('الملف الشخصي غير موجود');

      const profile = profileData as ProfileWithDept;

      // ── تحقق من حالة الحساب قبل التوجيه ─────────────────────
      if (profile.status === 'pending') {
        throw new Error('حسابك قيد المراجعة — يرجى انتظار موافقة المدير');
      }
      if (profile.status === 'suspended') {
        throw new Error('تم إيقاف حسابك — تواصل مع المدير');
      }
      if (profile.status === 'rejected') {
        throw new Error('تم رفض طلب حسابك — تواصل مع المدير');
      }

      // ── الخطوة 4: التوجيه ────────────────────────────────────
      setStep('جارٍ فتح لوحة التحكم...');
      // خزّن أولاً في store وlocalStorage قبل التنقل
      useAuthStore.getState().setProfile(profile);
      localStorage.setItem('munawb-profile', JSON.stringify(profile));
      // استخدم navigate بدلاً من window.location.href حتى لا يُعاد تحميل الصفحة
      // وتبقى بيانات الـ store محفوظة عند تشغيل beforeLoad
      await navigate({ to: getRoleRoute(profile.role) as '/' });

    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);

      if (msg.startsWith('timeout:signIn')) {
        setServerError('لا يمكن الوصول لخادم المصادقة — تحقق من اتصالك بالإنترنت أو جرب متصفحاً آخر');
      } else if (msg.startsWith('timeout:fetchProfile')) {
        setServerError('تم تسجيل الدخول لكن تعذّر تحميل البيانات — يرجى إعادة المحاولة');
      } else if (
        msg.includes('Invalid login') ||
        msg.includes('invalid_credentials') ||
        msg.toLowerCase().includes('invalid email or password')
      ) {
        setServerError('البريد الإلكتروني أو كلمة المرور غير صحيحة');
      } else if (msg.includes('Email not confirmed')) {
        setServerError('يرجى تأكيد بريدك الإلكتروني أولاً');
      } else if (msg.includes('الملف الشخصي غير موجود') || msg.toLowerCase().includes('no rows')) {
        setServerError('الحساب موجود لكن ملفك الشخصي غير مكتمل — تواصل مع المدير');
      } else {
        setServerError(`خطأ: ${msg}`);
      }
      setStep('');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950 p-4">
      <div className="glass-panel w-full max-w-md rounded-3xl p-8 shadow-2xl">

        {/* Logo + Title */}
        <div className="text-center mb-8">
          <img src="/munawb-logo.png" alt="مُناوب" className="w-16 h-16 mx-auto mb-4 rounded-2xl shadow-md" />
          <h1 className="text-2xl font-black text-slate-800 dark:text-white">مُناوِب</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-bold mt-1">
            نظام إدارة المناوبة الإلكتروني
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>

          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              البريد الإلكتروني
            </label>
            <input
              type="email"
              autoComplete="email"
              placeholder="example@hospital.com"
              className="input-field"
              dir="ltr"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-red-500 text-xs font-bold mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              كلمة المرور
            </label>
            <input
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              className="input-field"
              dir="ltr"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-red-500 text-xs font-bold mt-1">{errors.password.message}</p>
            )}
          </div>

          {serverError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 text-center">
              <p className="text-red-600 dark:text-red-400 text-sm font-bold">{serverError}</p>
            </div>
          )}

          <button type="submit" className="btn-primary w-full py-3 text-base" disabled={isSubmitting}>
            {isSubmitting ? (
              <><span className="spinner border-white/30 border-t-white" /> {step || 'جارٍ تسجيل الدخول...'}</>
            ) : (
              'تسجيل الدخول'
            )}
          </button>
        </form>

        <div className="mt-6 flex flex-col items-center gap-3 text-xs font-bold">
          <a href="/register" className="text-blue-600 dark:text-blue-400 hover:underline">
            طلب حساب جديد
          </a>
          <a href="/reset-password" className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:underline">
            نسيت كلمة المرور؟
          </a>
        </div>

      </div>
    </div>
  );
}
